import {
  Box,
  Button,
  FieldWrapper,
  FormField,
  FormGrid,
  Text,
  Textarea,
} from '@dixa/lib-shared-habla';
import { colors, sizes } from '@dixa/lib-shared-tokens';
import { useForm } from 'react-hook-form';

import { HookFormTextarea } from 'components/hookForm';

import styles from './jwtValidation.module.scss';

/** JWT validation component states. */
export type JwtValidationState =
  | { type: 'initialized' }
  | { type: 'decoding' }
  | {
      type: 'decoding-failure';
      error: {
        decrypted?: string;
        message: string;
      };
    }
  | {
      type: 'decoding-success';
      decrypted: string;
    };

const getDecodedComponent = (state: JwtValidationState): JSX.Element => {
  switch (state.type) {
    case 'initialized': {
      return (
        <FieldWrapper label="Decoded" noAlert>
          <Textarea placeholder="Result" />
        </FieldWrapper>
      );
    }
    case 'decoding': {
      return (
        <FieldWrapper label="Decoded" noAlert>
          <Textarea placeholder="Decoding..." />
        </FieldWrapper>
      );
    }
    case 'decoding-failure': {
      return (
        <FieldWrapper
          label="Decoded"
          meta={{ error: `JWT is not valid. ${state.error.message}` }}
        >
          <Textarea
            placeholder="Result"
            defaultValue={state.error.decrypted}
            status="error"
          />
        </FieldWrapper>
      );
    }
    case 'decoding-success': {
      return (
        <FieldWrapper label="Decoded" meta={{ success: 'JWT is valid' }}>
          <Textarea defaultValue={state.decrypted} status="success" />
        </FieldWrapper>
      );
    }
  }
};

export type JwtValidationProps = {
  /** Add classes to the root element. */
  className?: string;
  /** Component states. */
  state: JwtValidationState;
  /**
   * Triggers when decode jwt button is clicked.
   *
   * @param payload - Encoded token.
   */
  onDecrypt: (payload: string) => void;
};

/** Component which user can use to decode jwt. */
export function JwtValidation({
  className,
  state,
  onDecrypt,
}: JwtValidationProps): JSX.Element {
  const { control, handleSubmit, formState } = useForm<{ encoded: string }>();

  const decoded = getDecodedComponent(state);
  const isDecoding = state.type === 'decoding';

  return (
    <Box
      className={className}
      background={colors.neutral[100]}
      borderRadius={sizes['border-radius'][100]}
      padding={sizes.spacer[300]}
    >
      <Text.Heading variant="heading-3" className={styles.mb300}>
        JWT validation
      </Text.Heading>
      <FormGrid className={styles.form}>
        <FormField span={3} className={styles.encodedFormField}>
          <HookFormTextarea
            name="encoded"
            label="Encoded"
            control={control}
            componentProps={{
              placeholder: 'Insert your JWT',
            }}
            noAlert
          />

          <Button
            className={styles.decodeButton}
            variant="secondary"
            type="button"
            disabled={isDecoding || !formState.dirtyFields.encoded}
            onClick={handleSubmit((x) => onDecrypt(x.encoded))}
          >
            Decode JWT
          </Button>
        </FormField>
        <FormField span={3}>{decoded}</FormField>
      </FormGrid>
    </Box>
  );
}
